import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import {
  Mail, Send, Eye, PlayCircle, RotateCcw, ShieldOff, ShieldCheck,
  ToggleLeft, ToggleRight, Save, Users, AlertCircle, Check, Clock, Ban,
} from "lucide-react";

/**
 * Admin onboarding panel — Beta Follow-up campaign controls.
 *
 * Sections:
 *   1. Campaign toggle + stats overview
 *   2. Template editor (subject / body / reply_to) with preview + test-send
 *   3. Eligibility preview (read-only) with per-user suppress
 *   4. Job list with retry
 *   5. Audit trail
 */
export default function OnboardingPanel() {
  const [campaign, setCampaign] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [preview, setPreview] = useState(null);       // eligibility preview
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTpl, setActiveTpl] = useState(null);   // template being edited
  const [tplPreview, setTplPreview] = useState(null); // rendered {subject, body_html}
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, t, s, j, p, a] = await Promise.all([
        api.get("/admin/onboarding/campaigns"),
        api.get("/admin/onboarding/templates"),
        api.get("/admin/onboarding/stats"),
        api.get("/admin/onboarding/jobs"),
        api.get("/admin/onboarding/campaigns/beta_followup_v1/eligibility-preview"),
        api.get("/admin/onboarding/audit"),
      ]);
      setCampaign((c.data || []).find(x => x.key === "beta_followup_v1"));
      setTemplates(t.data || []);
      setStats(s.data);
      setJobs(j.data || []);
      setPreview(p.data);
      setAudit(a.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleCampaign = async () => {
    const willEnable = !campaign?.enabled;
    const confirmMsg = willEnable
      ? "Enable the Beta Follow-up campaign?\n\nEligible users will start receiving the follow-up email at their scheduled send date."
      : "Disable the Beta Follow-up campaign?\n\nAny scheduled jobs will be paused. No further emails will be sent until you re-enable.";
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.put(`/admin/onboarding/campaigns/${campaign.key}`, { enabled: willEnable });
      toast.success(willEnable ? "Campaign enabled" : "Campaign disabled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveTemplate = async () => {
    if (!activeTpl) return;
    try {
      await api.put(`/admin/onboarding/templates/${activeTpl.key}`, {
        subject: activeTpl.subject,
        body_html: activeTpl.body_html,
        reply_to: activeTpl.reply_to,
      });
      toast.success("Template saved");
      setActiveTpl(null);
      setTplPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const previewTemplate = async (key) => {
    try {
      const { data } = await api.post(
        `/admin/onboarding/templates/${key}/preview`,
        { sample_first_name: "Sarah" },
      );
      setTplPreview({ key, ...data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const testSend = async (key) => {
    if (!testTo.trim()) {
      toast.error("Enter a test recipient email address.");
      return;
    }
    try {
      await api.post(`/admin/onboarding/templates/${key}/test-send`, {
        to: testTo.trim(),
        sample_first_name: "Dwight",
      });
      toast.success(`Test email sent to ${testTo.trim()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const suppressUser = async (u) => {
    const reason = window.prompt(
      `Exclude ${u.name || u.email} from the Beta Follow-up?\n\nOptional reason:`,
      "",
    );
    if (reason === null) return;
    try {
      await api.post(`/admin/onboarding/users/${u.user_id}/suppress`, { reason });
      toast.success("User excluded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const unsuppressUser = async (u) => {
    try {
      await api.delete(`/admin/onboarding/users/${u.user_id}/suppress`);
      toast.success("User re-included");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const retryJob = async (job) => {
    try {
      await api.post(`/admin/onboarding/jobs/${job.id}/retry`);
      toast.success("Retry queued");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const runSweep = async () => {
    try {
      const { data } = await api.post("/admin/onboarding/sweep");
      toast.success(`Sweep complete: sent ${data.sent}, failed ${data.failed}, skipped ${data.skipped}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <p className="text-ink-500 text-sm">Loading onboarding panel…</p>;

  return (
    <div className="space-y-4" data-testid="admin-onboarding-panel">
      {/* Campaign toggle + stats */}
      <div className="card-base p-4 space-y-3" data-testid="onboarding-campaign-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-heading text-lg font-semibold flex items-center gap-2">
              <Mail size={18} className="text-sage-600" />
              {campaign?.name || "Beta follow-up"}
            </div>
            <div className="text-xs text-ink-500">
              Two-path onboarding email. 3-day feedback for users who logged in;
              7-day check-in for those who didn&apos;t.
            </div>
          </div>
          <button
            onClick={toggleCampaign}
            className={`btn-secondary !py-2 !px-3 inline-flex items-center gap-2 ${campaign?.enabled ? "text-sage-700" : "text-amber-deep"}`}
            data-testid="onboarding-toggle-campaign"
          >
            {campaign?.enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
            {campaign?.enabled ? "Enabled" : "Disabled"}
          </button>
        </div>
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <StatMini label="Eligible pending" value={stats.eligible_pending} icon={Users} testid="stat-eligible-pending" />
            <StatMini label="Scheduled" value={stats.scheduled} icon={Clock} testid="stat-scheduled" />
            <StatMini label="Sent" value={stats.sent} icon={Check} testid="stat-sent" />
            <StatMini label="Failed" value={stats.failed} icon={AlertCircle} testid="stat-failed" />
            <StatMini label="Suppressed" value={stats.suppressed} icon={Ban} testid="stat-suppressed" />
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={runSweep} className="btn-secondary !text-sm inline-flex items-center gap-1" data-testid="onboarding-run-sweep">
            <PlayCircle size={14} /> Run sweep now
          </button>
          <button onClick={load} className="btn-secondary !text-sm inline-flex items-center gap-1" data-testid="onboarding-refresh">
            <RotateCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Templates */}
      <div className="card-base p-4 space-y-3">
        <div className="font-heading text-lg font-semibold">Templates</div>
        <div className="text-xs text-ink-500 -mt-1">
          <code>{"{{first_name}}"}</code>, <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code> are substituted at send time.
        </div>
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.key} className="border border-ivory-200 rounded-xl p-3 space-y-2" data-testid={`onboarding-template-${t.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="text-xs text-ink-500">Key: <code>{t.key}</code></div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => previewTemplate(t.key)} className="btn-secondary !py-1.5 !px-2.5 !text-xs" data-testid={`onboarding-preview-${t.key}`}>
                    <Eye size={14} /> Preview
                  </button>
                  <button onClick={() => setActiveTpl(t)} className="btn-secondary !py-1.5 !px-2.5 !text-xs" data-testid={`onboarding-edit-${t.key}`}>
                    Edit
                  </button>
                </div>
              </div>
              <div className="text-xs text-ink-600">
                <div><strong>Subject:</strong> {t.subject}</div>
                <div><strong>Reply-to:</strong> {t.reply_to}</div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="Send test to email…"
                  className="input-field !py-1.5 !text-xs flex-1 min-w-[180px]"
                  data-testid={`onboarding-test-to-${t.key}`}
                />
                <button
                  onClick={() => testSend(t.key)}
                  className="btn-secondary !py-1.5 !px-2.5 !text-xs"
                  data-testid={`onboarding-test-send-${t.key}`}
                >
                  <Send size={14} /> Test send
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Template preview modal-lite */}
      {tplPreview && (
        <div className="card-base p-4 space-y-2" data-testid="onboarding-preview-panel">
          <div className="flex items-center justify-between">
            <div className="font-heading font-semibold">Preview · {tplPreview.key}</div>
            <button onClick={() => setTplPreview(null)} className="btn-ghost text-sm">Close</button>
          </div>
          <div className="text-sm"><strong>Subject:</strong> {tplPreview.subject}</div>
          <div className="text-sm"><strong>Reply-to:</strong> {tplPreview.reply_to}</div>
          <div
            className="border border-ivory-200 rounded-xl p-4 bg-white text-sm"
            dangerouslySetInnerHTML={{ __html: tplPreview.body_html }}
          />
        </div>
      )}

      {/* Template editor */}
      {activeTpl && (
        <div className="card-base p-4 space-y-3" data-testid="onboarding-editor">
          <div className="flex items-center justify-between">
            <div className="font-heading font-semibold">Edit · {activeTpl.name}</div>
            <button onClick={() => setActiveTpl(null)} className="btn-ghost text-sm">Cancel</button>
          </div>
          <label className="block">
            <div className="label-overline mb-1">Subject</div>
            <input
              value={activeTpl.subject || ""}
              onChange={(e) => setActiveTpl({ ...activeTpl, subject: e.target.value })}
              className="input-field w-full"
              data-testid="onboarding-editor-subject"
            />
          </label>
          <label className="block">
            <div className="label-overline mb-1">Reply-to</div>
            <input
              value={activeTpl.reply_to || ""}
              onChange={(e) => setActiveTpl({ ...activeTpl, reply_to: e.target.value })}
              className="input-field w-full"
              data-testid="onboarding-editor-reply-to"
            />
          </label>
          <label className="block">
            <div className="label-overline mb-1">Body HTML</div>
            <textarea
              rows={12}
              value={activeTpl.body_html || ""}
              onChange={(e) => setActiveTpl({ ...activeTpl, body_html: e.target.value })}
              className="input-field w-full font-mono text-xs"
              data-testid="onboarding-editor-body"
            />
          </label>
          <div className="flex gap-2">
            <button onClick={saveTemplate} className="btn-primary inline-flex items-center gap-1" data-testid="onboarding-editor-save">
              <Save size={14} /> Save
            </button>
          </div>
        </div>
      )}

      {/* Eligibility preview */}
      <div className="card-base p-4 space-y-2">
        <div className="font-heading text-lg font-semibold">Eligible users (read-only preview)</div>
        <div className="text-xs text-ink-500 -mt-1">
          This list is a snapshot. Opening it does NOT schedule any emails.
          Exclude anyone here before enabling the campaign.
        </div>
        {!preview?.users?.length ? (
          <p className="text-ink-500 text-sm">No users found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="onboarding-eligibility-table">
              <thead className="bg-ivory-50 text-ink-500 text-left">
                <tr>
                  <th className="p-2">User</th>
                  <th className="p-2">Verified</th>
                  <th className="p-2">Last login</th>
                  <th className="p-2">Path</th>
                  <th className="p-2">Scheduled for</th>
                  <th className="p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {preview.users.slice(0, 200).map((u) => (
                  <tr key={u.user_id} className="border-b border-ivory-100" data-testid={`onboarding-preview-row-${u.user_id}`}>
                    <td className="p-2">
                      <div className="font-medium">{u.name || "—"}</div>
                      <div className="text-ink-500">{u.email}</div>
                    </td>
                    <td className="p-2 text-ink-600">{fmtDate(u.verified_at)}</td>
                    <td className="p-2 text-ink-600">{u.last_login_at ? fmtDate(u.last_login_at) : <span className="text-ink-400">never</span>}</td>
                    <td className="p-2">
                      {u.path_key === "three_day_feedback" && <span className="pill bg-sage-100 text-sage-700 !text-[10px]">3-day</span>}
                      {u.path_key === "seven_day_getting_started" && <span className="pill bg-amber-soft text-amber-deep !text-[10px]">7-day</span>}
                      {!u.path_key && <span className="text-ink-400">—</span>}
                    </td>
                    <td className="p-2 text-ink-600">{u.scheduled_for ? fmtDate(u.scheduled_for) : "—"}</td>
                    <td className="p-2">
                      {u.suppressed && <span className="pill bg-terracotta-100 text-terracotta-700 !text-[10px]">suppressed</span>}
                      {u.already_job_status === "sent" && <span className="pill bg-sage-100 text-sage-700 !text-[10px]">sent</span>}
                      {u.already_job_status === "scheduled" && <span className="pill bg-amber-soft text-amber-deep !text-[10px]">scheduled</span>}
                      {!u.suppressed && !u.already_job_status && u.eligible && <span className="text-ink-500">pending</span>}
                      {u.requires_approval && <span className="pill bg-amber-soft text-amber-deep !text-[10px] ml-1">needs approval</span>}
                      {!u.eligible && u.reason && <span className="text-ink-400" title={u.reason}>—</span>}
                    </td>
                    <td className="p-2">
                      {!u.suppressed ? (
                        <button onClick={() => suppressUser(u)} className="btn-secondary !py-1 !px-2 !text-[10px]" data-testid={`onboarding-suppress-${u.user_id}`}>
                          <ShieldOff size={12} /> Exclude
                        </button>
                      ) : (
                        <button onClick={() => unsuppressUser(u)} className="btn-secondary !py-1 !px-2 !text-[10px]" data-testid={`onboarding-unsuppress-${u.user_id}`}>
                          <ShieldCheck size={12} /> Include
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Jobs */}
      <div className="card-base p-4 space-y-2">
        <div className="font-heading text-lg font-semibold">Jobs</div>
        {jobs.length === 0 ? (
          <p className="text-ink-500 text-sm">No jobs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-ivory-50 text-ink-500 text-left">
                <tr>
                  <th className="p-2">User</th>
                  <th className="p-2">Path</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Scheduled</th>
                  <th className="p-2">Sent</th>
                  <th className="p-2">Attempts</th>
                  <th className="p-2">Last error</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.slice(0, 100).map((j) => (
                  <tr key={j.id} className="border-b border-ivory-100" data-testid={`onboarding-job-${j.id}`}>
                    <td className="p-2">
                      <div className="font-medium">{j.user_name || "—"}</div>
                      <div className="text-ink-500">{j.user_email}</div>
                    </td>
                    <td className="p-2">{j.path_key}</td>
                    <td className="p-2">
                      <span className={`pill !text-[10px] ${statusPillClass(j.status)}`}>{j.status}</span>
                    </td>
                    <td className="p-2 text-ink-600">{fmtDate(j.scheduled_for)}</td>
                    <td className="p-2 text-ink-600">{j.sent_at ? fmtDate(j.sent_at) : "—"}</td>
                    <td className="p-2 text-ink-600">{j.attempts}</td>
                    <td className="p-2 text-ink-500 max-w-[240px] truncate" title={j.last_error || ""}>{j.last_error || "—"}</td>
                    <td className="p-2">
                      {j.status !== "sent" && (
                        <button onClick={() => retryJob(j)} className="btn-secondary !py-1 !px-2 !text-[10px]" data-testid={`onboarding-retry-${j.id}`}>
                          <RotateCcw size={12} /> Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Audit */}
      <div className="card-base p-4 space-y-2">
        <div className="font-heading text-lg font-semibold">Audit trail</div>
        {audit.length === 0 ? (
          <p className="text-ink-500 text-sm">No activity yet.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {audit.slice(0, 50).map((a) => (
              <li key={a.id} className="border-b border-ivory-100 py-1.5">
                <span className="font-medium">{a.event}</span>
                <span className="text-ink-500"> · {a.actor}</span>
                {a.user_id && <span className="text-ink-500"> · user {a.user_id.slice(-6)}</span>}
                <span className="text-ink-400 ml-2">{fmtDate(a.at)}</span>
                {a.detail && Object.keys(a.detail).length > 0 && (
                  <div className="text-ink-500 pl-2">{JSON.stringify(a.detail)}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatMini({ label, value, icon: Icon, testid }) {
  return (
    <div className="card-base p-2.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-ink-500 text-[10px] uppercase tracking-wide">
        <Icon size={12} /> {label}
      </div>
      <div className="font-heading text-2xl font-semibold">{value ?? 0}</div>
    </div>
  );
}

function statusPillClass(s) {
  switch (s) {
    case "sent": return "bg-sage-100 text-sage-700";
    case "scheduled": return "bg-amber-soft text-amber-deep";
    case "failed": return "bg-terracotta-100 text-terracotta-700";
    case "skipped": return "bg-ivory-200 text-ink-600";
    default: return "bg-ivory-100 text-ink-700";
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
}
